
import { defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

import CompanySchedules from '@/modules/CompaniesModule/components/CompanySchedules/index.vue'

// validations
import useVuelidate from '@vuelidate/core'
import { required, requiredIf, integer, email } from '@vuelidate/validators'
import { incorrectSymbolValidator, binValidator } from '@/common/validators/common.validators'

// types
import {
    CompanyT,
    CountryT,
    CompanyPayloadT,
    BusinessModelT,
    CompanyWorkSchedule,
    CompanySource,
} from '@/core/types/companies.types'
import { BillingPeriodT, ManagerT, UserT } from '@/core/types/users.types'

// constants
import {
    CompaniesTypes,
    COMPANY_B2B_DEFAULT_GRAPH,
    COMPANY_B2C_DEFAULT_GRAPH,
} from '@/core/constants/companies.constants'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { companies } from '@/core/realisations/companies.realisation'
import { users } from '@/core/realisations/users.realisation'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useList } from '@/common/composable/useList'

import { BILLING_TARIFF_TYPES } from '@/core/constants/billing.constants'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-input': TheInput,
        'the-select': TheSelect,
        'the-button': TheButton,
        checkbox: Checkbox,
        'company-schedules': CompanySchedules,
    },
    props: {
        isCreate: {
            type: Boolean,
            default: true,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<CompanyT>,
            default: null,
        },
    },
    emits: ['cancel', 'create', 'update'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { loadList, loadListMore } = useList()

        const state = reactive({
            id: undefined as number | undefined,
            name: null as string | null,
            short_name: null as string | null,
            bin: null as string | null,
            kbe: null as number | null,
            type_id: null as number | null,
            country_id: null as number | null,
            manager_id: null as number | null,
            initiatorId: null as number | null,
            jur_address: null as string | null,
            fact_address: null as string | null,
            nds: null as string | null,
            ndsRegisterDate: null as string | null,
            ndsSeries: null as string | null,
            bank_account: null as string | null,
            bank: null as string | null,
            bik: null as string | null,
            contract_number: null as string | null,
            contract_date: null as string | null,
            contact_name: null as string | null,
            contact_phone: null as string | null,
            contact_email: null as string | null,
            billingPeriodId: null as number | null,
            businessModelId: null as number | null,
            companySourceId: null as number | null,
            billingEnabled: false,
            isStandardContract: false,
            isSubdivision: false,
            priority: 0,
            accountant_id: null as number | null,
            shipmentTariffId: null as number | null,
            schedules: [] as CompanyWorkSchedule[],
            isChl: false,
        })

        const CompaniesActionsValidation = {
            name: { required, incorrectSymbolValidator },
            short_name: { required, incorrectSymbolValidator },
            bin: { required, integer, binValidator },
            kbe: { required, integer },
            type_id: { required, integer },
            country_id: { required, integer },
            manager_id: { required, integer },
            initiatorId: { required: requiredIf(() => props.isCreate), integer },
            jur_address: { required, incorrectSymbolValidator },
            fact_address: { required, incorrectSymbolValidator },
            nds: { required },
            bank_account: { required, incorrectSymbolValidator },
            bank: { required, incorrectSymbolValidator },
            bik: { required, incorrectSymbolValidator },
            contract_number: { required },
            contact_name: { required, incorrectSymbolValidator },
            contact_phone: { required, incorrectSymbolValidator },
            contact_email: { required, email, incorrectSymbolValidator },
            businessModelId: { required },
            companySourceId: { required },
            accountant_id: {
                required: requiredIf(() => state.billingEnabled),
            },
        }

        const state_form$ = useVuelidate(CompaniesActionsValidation, state)

        const emitCancel = () => {
            emit('cancel')
        }

        const isSchedulesInvalid = (): boolean => {
            if (state.schedules.length === 0) {
                notifications.notify({
                    title: 'Для создания компании необходимо заполнить хотя бы 1 рабочий день',
                    type: 'danger',
                })
                return true
            }

            if (state.schedules.filter((schedule) => schedule.scheduleTypeId === 15).length === 0) {
                notifications.notify({
                    title: 'Для создания компании необходимо заполнить хотя бы 1 рабочий день отправителя',
                    type: 'danger',
                })
                return true
            }

            if (state.schedules.filter((schedule) => schedule.scheduleTypeId === 16).length === 0) {
                notifications.notify({
                    title: 'Для создания компании необходимо заполнить хотя бы 1 рабочий день получателя',
                    type: 'danger',
                })
                return true
            }

            return false
        }

        const emitManager = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (
                !state.name ||
                !state.short_name ||
                !state.bank ||
                !state.bank_account ||
                !state.bik ||
                !state.bin ||
                !state.nds ||
                !state.type_id ||
                !state.country_id ||
                !state.contract_number ||
                !state.contact_email ||
                !state.contact_phone ||
                !state.contact_name ||
                !state.jur_address ||
                !state.fact_address ||
                !state.kbe ||
                !state.manager_id
            )
                return

            let company_payload: CompanyPayloadT = {
                name: state.name,
                shortName: state.short_name,
                bank: state.bank,
                bankAccount: state.bank_account,
                bik: state.bik,
                bin: state.bin,
                ndsNumber: state.nds,
                ndsRegisterDate: state.ndsRegisterDate,
                ndsSeries: state.ndsSeries,
                type: state.type_id,
                countryId: state.country_id,
                contractNumber: state.contract_number,
                contractDate: state.contract_date,
                contactEmail: state.contact_email,
                contactPhone: state.contact_phone,
                contactName: state.contact_name,
                jurAddress: state.jur_address,
                factAddress: state.fact_address,
                kbe: state.kbe,
                managerId: state.manager_id,
                id: state.id,
                billingPeriodId: state.billingPeriodId,
                businessModelId: state.businessModelId,
                companySourceId: state.companySourceId as number,
                billingEnabled: state.billingEnabled,
                isStandardContract: state.isStandardContract,
                priority: state.priority,
                accountantId: state.accountant_id,
                isChl: state.isChl
            }

            if (state.shipmentTariffId) {
                company_payload.shipmentTariffId = state.shipmentTariffId
            }

            if (props.isCreate) {
                company_payload.initiatorId = state.initiatorId

                if (isSchedulesInvalid()) {
                    return
                }

                emit('create', {
                    ...toRaw(company_payload),
                    schedules: state.schedules,
                })

                return
            }

            if (props.isUpdate) {
                company_payload.isSubdivision = state.isSubdivision
                emit('update', toRaw({ company_payload }))
            }
        }

        const data_state = reactive({
            company_sources: new DataState<CompanySource>({}),
            countries: new DataState<CountryT>({}),
            managers: new DataState<ManagerT>({}),
            initiators: new DataState<ManagerT>({}),
            billing_periods: new DataState<BillingPeriodT>({}),
            business_models: new DataState<BusinessModelT>({}),
            accountants: new DataState<UserT>({}),
        })

        // ACCOUNTANTS

        const loadAccountants = async (page: number, name?: string) => {
            data_state.accountants.is_loading = true

            const response = await users.getAccountants(page, name, 1),
                params = { data_state: data_state.accountants, response, page }
            loadList(params)
        }

        const loadMoreAccountants = async (page: number) => {
            if (page > data_state.accountants.pagination.total_pages) return

            const response = await users.getAccountants(page),
                params = { data_state: data_state.accountants, response, page }
            loadListMore(params)
        }

        const searchAccountants = async (search_query: string) => {
            await loadAccountants(1, search_query)
        }

        const unshiftFindedAccountant = async (initiator: ManagerT) => {
            await loadInitiators(data_state.initiators.pagination.current_page)
            data_state.initiators.list.unshift(initiator)
        }

        // COUNTRIES

        const loadCompanySources = async () => {
            // data_state.countries.pagination.current_page = 1

            try {
                data_state.company_sources.is_loading = true

                const response = await companies.getCompanySources()

                data_state.company_sources.list = response.data

                data_state.company_sources.is_failed = false
                data_state.company_sources.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.company_sources.failure.catchError(error)

                if (!data_state.company_sources.failure.error_state.is_cancel) {
                    data_state.company_sources.is_failed = true
                }
            } finally {
                if (!data_state.company_sources.failure.error_state.is_cancel) {
                    data_state.company_sources.is_loading = false
                }
            }
        }

        // COUNTRIES

        const loadCountries = async (page: number, search?: string) => {
            data_state.countries.pagination.current_page = page

            try {
                data_state.countries.is_loading = true

                const response = await companies.getCountries(page, search)

                data_state.countries.list = response.data
                data_state.countries.pagination.total_pages = response.meta.last_page

                data_state.countries.is_failed = false
                data_state.countries.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.countries.failure.catchError(error)

                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_failed = true
                }
            } finally {
                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_loading = false
                }
            }
        }

        const loadMoreCountries = async (page: number) => {
            if (page > data_state.countries.pagination.total_pages) return
            data_state.countries.pagination.current_page = page

            try {
                data_state.countries.is_loading = true

                const response = await companies.getCountries(page)
                data_state.countries.list = data_state.countries.list.concat(response.data)

                data_state.countries.pagination.total_pages = response.meta.last_page

                data_state.countries.is_failed = false
                data_state.countries.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.countries.failure.catchError(error)

                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_failed = true
                }
            } finally {
                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_loading = false
                }
            }
        }

        const searchCountries = async (search_query: string) => {
            // data_state.countries.pagination.current_page
            await loadCountries(1, search_query)
        }

        const unshiftFindedCountry = async (country: CountryT) => {
            await loadCountries(data_state.countries.pagination.current_page)
            data_state.countries.list.unshift(country)
        }

        // MANAGERS

        const loadManagers = async (page: number, search?: string) => {
            data_state.managers.pagination.current_page = page

            try {
                data_state.managers.is_loading = true

                const response = await users.getManagers({ page, name: search, statusId: 1 })

                data_state.managers.list = response.data
                data_state.managers.pagination.total_pages = response.meta.last_page

                data_state.managers.is_failed = false
                data_state.managers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.managers.failure.catchError(error)

                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_failed = true
                }
            } finally {
                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_loading = false
                }
            }
        }

        const loadMoreManagers = async (page: number) => {
            if (page > data_state.managers.pagination.total_pages) return
            data_state.managers.pagination.current_page = page

            try {
                data_state.managers.is_loading = true

                const response = await users.getManagers({ page })
                data_state.managers.list = data_state.managers.list.concat(response.data)

                data_state.managers.pagination.total_pages = response.meta.last_page

                data_state.managers.is_failed = false
                data_state.managers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.managers.failure.catchError(error)

                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_failed = true
                }
            } finally {
                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_loading = false
                }
            }
        }

        const searchManagers = async (search_query: string) => {
            // data_state.managers.pagination.current_page
            await loadManagers(1, search_query)
        }

        const unshiftFindedManager = async (manager: ManagerT) => {
            await loadManagers(data_state.managers.pagination.current_page)
            data_state.managers.list.unshift(manager)
        }

        // INITIATORS

        const loadInitiators = async (page: number, search?: string) => {
            data_state.initiators.pagination.current_page = page

            try {
                data_state.initiators.is_loading = true

                const response = await users.getInitiators({ page, name: search })

                data_state.initiators.list = response.data
                data_state.initiators.pagination.total_pages = response.meta.last_page

                data_state.initiators.is_failed = false
                data_state.initiators.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.initiators.failure.catchError(error)

                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_failed = true
                }
            } finally {
                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_loading = false
                }
            }
        }

        const loadMoreInitiators = async (page: number) => {
            if (page > data_state.initiators.pagination.total_pages) return
            data_state.initiators.pagination.current_page = page

            try {
                data_state.initiators.is_loading = true

                const response = await users.getInitiators({ page })
                data_state.initiators.list = data_state.initiators.list.concat(response.data)

                data_state.initiators.pagination.total_pages = response.meta.last_page

                data_state.initiators.is_failed = false
                data_state.initiators.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.initiators.failure.catchError(error)

                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_failed = true
                }
            } finally {
                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_loading = false
                }
            }
        }

        const searchInitiators = async (search_query: string) => {
            // data_state.initiators.pagination.current_page
            await loadInitiators(1, search_query)
        }

        const unshiftFindedInitiator = async (initiator: ManagerT) => {
            await loadInitiators(data_state.initiators.pagination.current_page)
            data_state.initiators.list.unshift(initiator)
        }

        //

        const loadBillingPeriods = async () => {
            data_state.billing_periods.pagination.current_page = 1

            try {
                data_state.billing_periods.is_loading = true

                const response = await users.getBillingPeriods(1)

                data_state.billing_periods.list = response.data
                data_state.billing_periods.pagination.total_pages = 1

                data_state.billing_periods.is_failed = false
                data_state.billing_periods.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.billing_periods.failure.catchError(error)

                if (!data_state.billing_periods.failure.error_state.is_cancel) {
                    data_state.billing_periods.is_failed = true
                }
            } finally {
                if (!data_state.billing_periods.failure.error_state.is_cancel) {
                    data_state.billing_periods.is_loading = false
                }
            }
        }

        // Schedules
        const initializeSchedules = () => {
            if (props.isUpdate) return

            if (!state.businessModelId) {
                state.businessModelId = data_state.business_models.list[0].id
            }

            if (state.businessModelId === 1) {
                const sender_schedules = COMPANY_B2B_DEFAULT_GRAPH.map((schedule) => {
                    return {
                        ...schedule,
                        scheduleTypeId: 15,
                    }
                })

                const receiver_schedules = COMPANY_B2B_DEFAULT_GRAPH.map((schedule) => {
                    return {
                        ...schedule,
                        scheduleTypeId: 16,
                    }
                })

                state.schedules = sender_schedules.concat(receiver_schedules)
            } else if (state.businessModelId === 2) {
                const sender_schedules = COMPANY_B2C_DEFAULT_GRAPH.map((schedule) => {
                    return {
                        ...schedule,
                        scheduleTypeId: 15,
                    }
                })

                const receiver_schedules = COMPANY_B2C_DEFAULT_GRAPH.map((schedule) => {
                    return {
                        ...schedule,
                        scheduleTypeId: 16,
                    }
                })

                state.schedules = sender_schedules.concat(receiver_schedules)
            }
        }

        const onScheduleRemove = ({
            scheduleTypeId,
            weekday,
        }: {
            scheduleTypeId: number
            weekday: number
        }) => {
            const index = state.schedules.findIndex(
                (schedule) =>
                    schedule.scheduleTypeId === scheduleTypeId && schedule.weekday == weekday
            )
            if (index < 0) return
            state.schedules.splice(index, 1)
        }

        const onScheduleAdd = ({
            scheduleTypeId,
            weekday,
        }: {
            scheduleTypeId: number
            weekday: number
        }) => {
            if (state.businessModelId === 1) {
                const schedule = { ...COMPANY_B2B_DEFAULT_GRAPH[0] }
                schedule.weekday = weekday

                state.schedules.push({
                    ...schedule,
                    scheduleTypeId,
                })
            } else if (state.businessModelId === 2) {
                const schedule = { ...COMPANY_B2C_DEFAULT_GRAPH[0] }
                schedule.weekday = weekday

                state.schedules.push({
                    ...schedule,
                    scheduleTypeId,
                })
            }
        }

        const loadBusinessModels = async () => {
            data_state.business_models.pagination.current_page = 1

            try {
                data_state.business_models.is_loading = true

                const response = await companies.getBusinessModels()

                data_state.business_models.list = response.data
                data_state.business_models.pagination.total_pages = 1

                data_state.business_models.is_failed = false
                data_state.business_models.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.business_models.failure.catchError(error)

                if (!data_state.business_models.failure.error_state.is_cancel) {
                    data_state.business_models.is_failed = true
                }
            } finally {
                if (!data_state.business_models.failure.error_state.is_cancel) {
                    data_state.business_models.is_loading = false
                }
            }
        }

        // reading initial value on update
        onBeforeMount(() => {
            loadCompanySources()
            loadCountries(1)

            loadManagers(1)
            loadInitiators(1)

            loadAccountants(1)
            loadBillingPeriods()
            loadBusinessModels()

            if (!props.initialValue) return

            state.name = props.initialValue.name
            state.short_name = props.initialValue.short_name
            state.bin = props.initialValue.bin
            state.kbe = props.initialValue.kbe
            state.type_id = props.initialValue.type_id
            state.country_id = props.initialValue.country_id
            state.manager_id = props.initialValue.manager.id
            state.jur_address = props.initialValue.jur_address
            state.fact_address = props.initialValue.fact_address
            state.nds = props.initialValue.nds_number
            state.ndsRegisterDate = props.initialValue.nds_register_date
            state.ndsSeries = props.initialValue.nds_series
            state.bank_account = props.initialValue.bank_account
            state.bank = props.initialValue.bank
            state.bik = props.initialValue.bik
            state.contract_number = props.initialValue.contract_number
            state.contract_date = props.initialValue.contract_date
            state.contact_name = props.initialValue.contact_name
            state.contact_phone = props.initialValue.contact_phone
            state.contact_email = props.initialValue.contact_email
            state.id = props.initialValue.id
            state.billingEnabled = props.initialValue.billing_enabled
            state.billingPeriodId = props.initialValue.billing_period?.id || null
            state.businessModelId = props.initialValue.business_model_id
            state.isStandardContract = props.initialValue.is_standard_contract
            state.isSubdivision = props.initialValue.is_subdivision
            state.priority = props.initialValue.priority
            state.companySourceId = props.initialValue.company_source_id
            state.isChl = props.initialValue.isChl

            if (props.initialValue.accountant) {
                state.accountant_id = props.initialValue.accountant.id
            }
        })

        return {
            state,
            state_form$,
            emitCancel,
            emitManager,
            data_state,
            CompaniesTypes,
            // COMPANY RESOURCES
            loadCompanySources,
            // COUNTRIES
            loadCountries,
            loadMoreCountries,
            searchCountries,
            unshiftFindedCountry,
            // MANAGERS
            loadManagers,
            loadMoreManagers,
            searchManagers,
            unshiftFindedManager,
            // INITIATORS
            loadInitiators,
            loadMoreInitiators,
            searchInitiators,
            unshiftFindedInitiator,
            //
            loadBillingPeriods,
            loadBusinessModels,
            // ACCOUNTANTS
            loadAccountants,
            loadMoreAccountants,
            searchAccountants,
            unshiftFindedAccountant,
            //
            onScheduleRemove,
            onScheduleAdd,
            initializeSchedules,
            BILLING_TARIFF_TYPES,
        }
    },
})
